import { IconDefinition as RegularIconDefinition } from '@fortawesome/free-regular-svg-icons';
import { IconDefinition as SolidIconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Button, Dropdown } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { CloudFunctions, getFunctionsUrl } from '../../utils/utils';
import styles from './Topbar.module.scss';

export interface ButtonDefinition {
  url?: string;
  externalUrl?: string;
  externalTokenizedUrl?: boolean;
  icon?: SolidIconDefinition | RegularIconDefinition;
  label?: string;
  condition?: () => boolean;
  normalClass?: string;
  activeClass?: string;
  skipDefaultClass?: boolean;
  onClick?: () => void;
  isDropdown?: boolean;
  dropDownOptions?: ButtonDefinition[];
}

/** Mostra un'azione o un menu a tendina configurato per la topbar. */
const TopbarButton = ({
  buttonDefinition,
  pagePath,
  margin = true,
  isPublicPage = false,
}: {
  buttonDefinition: ButtonDefinition;
  pagePath: string;
  margin?: boolean;
  isPublicPage?: boolean;
}) => {
  const {
    url,
    icon = null,
    label,
    normalClass = '',
    activeClass = '',
    skipDefaultClass,
    externalUrl,
    externalTokenizedUrl,
    isDropdown = false,
    dropDownOptions = [],
  } = buttonDefinition;
  const { currentUser } = useFirebase();
  const history = useHistory();

  if (isDropdown && !dropDownOptions.find((option) => !option.condition || option.condition())) return null;

  return !isPublicPage ? (
    <>
      {isDropdown ? (
        <Dropdown
          className={classNames('borderless', normalClass, {
            [styles.topbarButton]: !skipDefaultClass,
            'mr-1': margin,
            [activeClass]: pagePath == url,
            'd-inline-block': true,
          })}
        >
          <Dropdown.Toggle
            as={Button}
            variant={'outline-light'}
            disabled={pagePath == url}
            active={pagePath == url}
            className={classNames('borderlessTransparent', normalClass, {
              [styles.topbarButton]: !skipDefaultClass,
              'mr-1': margin,
              [activeClass]: pagePath == url,
            })}
          >
            <span className={'nowrap'}>
              {!!icon && <FontAwesomeIcon icon={icon} className="me-2" />}
              {label && <span className={`d-none d-xl-inline-block`}>{label}</span>}
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu align={'right'}>
            {dropDownOptions
              .map((option, i) => {
                return !option.condition || option.condition() ? (
                  <TopbarDropdownNavItem
                    key={i.toString()}
                    pagePath={pagePath}
                    url={option.url}
                    label={option.label}
                    onClick={option.onClick}
                    button={option}
                  />
                ) : null;
              })
              .filter(Boolean)}
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <Button
          onClick={async () => {
            if (externalUrl) window.open(externalUrl, '_blank');
            else if (externalTokenizedUrl) {
              const url = new URL(getFunctionsUrl(CloudFunctions.GetFadAuthenticatedUrl));
              url.searchParams.append('uid', currentUser.uid);
              url.searchParams.append('token', await currentUser.getIdToken(true));
              url.searchParams.append('email', currentUser.email);
              window.location.href = url.toString();
            } else history.push(url);
          }}
          variant={'outline-light'}
          disabled={pagePath == url}
          active={pagePath == url}
          className={classNames('borderless', normalClass, { [styles.topbarButton]: !skipDefaultClass, 'mr-1': margin, [activeClass]: pagePath == url })}
        >
          <div className={'nowrap'}>
            {!!icon && <FontAwesomeIcon icon={icon} />}
            {label && <span className={`d-none d-xl-inline-block ${icon ? ' ml-0 ml-lg-2' : ''}`}>{label}</span>}
          </div>
        </Button>
      )}
    </>
  ) : (
    <Button
      as={Link}
      to={url}
      variant={'outline-light'}
      disabled={pagePath == url}
      active={pagePath == url}
      className={classNames('borderless', normalClass, { [styles.topbarButton]: !skipDefaultClass, 'mr-1': margin, [activeClass]: pagePath == url })}
    >
      <div className={'nowrap'}>
        {!!icon && <FontAwesomeIcon icon={icon} />}
        {label && <span className={`d-none d-xl-inline-block ${icon ? ' ml-0 ml-lg-2' : ''}`}>{label}</span>}
      </div>
    </Button>
  );
};

/** Mostra una voce di navigazione all'interno di un menu a tendina della topbar. */
const TopbarDropdownNavItem = ({ url, pagePath, label, onClick = null, button = null }) => {
  const { currentUser } = useFirebase();
  if (!!button && button.externalUrl) {
    return (
      <Dropdown.Item onClick={() => window.open(button.externalUrl, '_blank')}>
        {label}
      </Dropdown.Item>
    );
  }

  return !!button && button.externalTokenizedUrl ? (
    <Dropdown.Item
      disabled={pagePath == url}
      onClick={async () => {
        const url = new URL(getFunctionsUrl(CloudFunctions.GetFadAuthenticatedUrl));
        url.searchParams.append('uid', currentUser.uid);
        url.searchParams.append('token', await currentUser.getIdToken(true));
        url.searchParams.append('email', currentUser.email);
        window.location.href = url.toString();
      }}
    >
      <span className={pagePath == url ? 'text-primary' : ''}>{label}</span>
    </Dropdown.Item>
  ) : (
    <Dropdown.Item as={Link} to={url} disabled={pagePath == url} onClick={onClick}>
      <span className={pagePath == url ? 'text-primary' : ''}>{label}</span>
    </Dropdown.Item>
  );
};

export { TopbarButton, TopbarDropdownNavItem };
